import { ref, watch, computed  } from 'vue';
import { defineStore } from 'pinia';
import { useI18n } from "vue-i18n";
import { useGarageStore } from '@/stores/garage.js';
import { useAuthenticationStore } from '@/stores/authentication.js';

export const useModalStore = defineStore('modal', () => {
  // I18n
  const { t } = useI18n();

  // Stores
  const garageStore = useGarageStore();
  const authStore = useAuthenticationStore();

  // Variables
  const modalVisibility = ref(false);
  const modalType = ref("");
  const modalTypeExtraParam = ref(null);
  const modalTitle = ref("");
  const modalBody = ref("");
  const backButtonText = ref("");
  const backButtonLink = ref("");
  const negativeButtonText = ref("");
  const positiveButtonText = ref("");
  const positiveHandleTrigger = ref(false);
  const modalErrors = ref([]);
  const validationErrors = ref({});
  const image = ref(null);
  const positiveButtonDisabled = ref(false);

  const currentStripeProduct = ref("");

  // Modal Types
  const modalTypeConstants = ref({
    LOGIN: 'login',
    SIGN_UP: 'sign_up',
    TERMS_AND_CONDITIONS: 'terms_and_conditions',
    FORGOT_PASSWORD: 'forgot_password',
    ADD_VEHICLE: 'add_vehicle',
    EDIT_VEHICLE: 'edit_vehicle',
    GARAGE_INFO: 'garage_info',
    VEHICLE_INFO: 'vehicle_info',
    VEHICLE_IMAGE: 'vehicle_image',
    DRIVETRAIN_INFO: 'drivetrain_info',
    DESIGN_INFO: 'design_info',
    ADD_ADDRESS: 'add_address',
    EDIT_ADDRESS: 'edit_address',
    ADD_COMPLETED_SERVICE: 'add_completed_service',
    DELETE_ACCOUNT: 'delete_account',
    SIGN_UP_VERIFICATION: 'sign_up_verification',
    RESEND_VERIFICATION: 'resend_verification',
    CHANGE_PASSWORD_CONFIRMATION_NOTICE: 'change_password_confirmation_notice',
    STRIPE_PAYMENT: 'stripe_payment',
    CONFIRM_MOVE_TO_SERVICE_HISTORY: 'confirm_move_to_service_history',
    CONFIRM_CLEAR_REQUESTED_SERVICE_PROVIDER: 'confirm_clear_requested_service_provider'
  })

  // These specific modals will remain smaller on all screen sizes
  const smallWidthModals = ref([
    modalTypeConstants.value.LOGIN,
    modalTypeConstants.value.SIGN_UP,
    modalTypeConstants.value.DELETE_ACCOUNT,
    modalTypeConstants.value.TERMS_AND_CONDITIONS,
    modalTypeConstants.value.FORGOT_PASSWORD,
    modalTypeConstants.value.SIGN_UP_VERIFICATION,
    modalTypeConstants.value.RESEND_VERIFICATION,
    modalTypeConstants.value.CHANGE_PASSWORD_CONFIRMATION_NOTICE,
    modalTypeConstants.value.STRIPE_PAYMENT,
    modalTypeConstants.value.CONFIRM_MOVE_TO_SERVICE_HISTORY,
    modalTypeConstants.value.CONFIRM_CLEAR_REQUESTED_SERVICE_PROVIDER
  ]);

  // One off modal variables
  const newVehicle = ref(null);
  const newGarage = ref(null);
  const newUserAddress = ref(null);
  const userEmail = ref(null);
  const userVerificationPassword = ref(null); // Only used to resend the verification email, DO NOT USE to store the user's password
  const moveToServiceHistoryQuote = ref(null);

  const $reset = () => {
      positiveButtonDisabled.value = false;
  }

  const setModal = (modal, extraParam = null) => {
    if(!(modal === modalTypeConstants.value.SIGN_UP && modalType.value === modalTypeConstants.value.TERMS_AND_CONDITIONS) || modal === modalTypeConstants.value.TERMS_AND_CONDITIONS) {
      resetModal();
    }

    modalType.value = modal;
    modalTypeExtraParam.value = extraParam;
    positiveButtonDisabled.value = false;

    switch (modalType.value) {
      case modalTypeConstants.value.FORGOT_PASSWORD:
      setModalBackLink(modalTypeConstants.value.LOGIN);
      break;
      case modalTypeConstants.value.ADD_VEHICLE:
      setNewVehicle(JSON.parse(JSON.stringify(garageStore.emptyVehicle)));
      break;
      case modalTypeConstants.value.EDIT_VEHICLE:
      setNewVehicle(modalTypeExtraParam.value);
      garageStore.getVehicleDropdowns();
      break;
      case modalTypeConstants.value.GARAGE_INFO:
      setNewGarage(JSON.parse(JSON.stringify(modalTypeExtraParam.value)));
      break;
      case modalTypeConstants.value.VEHICLE_INFO:
      setNewVehicle(JSON.parse(JSON.stringify(modalTypeExtraParam.value)));
      break;
      case modalTypeConstants.value.DRIVETRAIN_INFO:
      setNewVehicle(JSON.parse(JSON.stringify(modalTypeExtraParam.value)));
      break;
      case modalTypeConstants.value.DESIGN_INFO:
      setNewVehicle(JSON.parse(JSON.stringify(modalTypeExtraParam.value)));
      break;
      case modalTypeConstants.value.ADD_ADDRESS:
      setNewUserAddress(JSON.parse(JSON.stringify(modalTypeExtraParam.value)));
      break;
      case modalTypeConstants.value.EDIT_ADDRESS:
      setNewUserAddress(JSON.parse(JSON.stringify(modalTypeExtraParam.value)));
      break;
      case modalTypeConstants.value.RESEND_VERIFICATION:
      setModalBackLink(modalTypeConstants.value.LOGIN);
      break;
      case modalTypeConstants.value.CHANGE_PASSWORD_CONFIRMATION_NOTICE:
      setModalBackLink(modalTypeConstants.value.LOGIN);
      break;
      case modalTypeConstants.value.CONFIRM_MOVE_TO_SERVICE_HISTORY:
      case modalTypeConstants.value.CONFIRM_CLEAR_REQUESTED_SERVICE_PROVIDER:
      break;
      default:
      break;
    }
    setModalAttributes();
  };

  const setModalVisibility = (value) => {
    modalVisibility.value = value;
  };

  const setModalTitle = (value) => {
    modalTitle.value = value;
  };

  const setModalBody = (value) => {
    modalBody.value = value;
  };

  const setModalNegativeText = (value) => {
    negativeButtonText.value = value;
  };

  const setModalBackText = (value) => {
    backButtonText.value = value;
  };

  const setModalBackLink = (value) => {
    backButtonLink.value = value;
  };

  const setModalPositiveText = (value) => {
    positiveButtonText.value = value;
  };

  const setModalErrors = (value) => {
    modalErrors.value = value;
  };

  const setPositiveHandleTrigger = (value) => {
    positiveHandleTrigger.value = value;
  };

  const setValidationErrors = (value) => {
    validationErrors.value = value;
  };

  const setModalAttributes = () => {
    setModalTitle(t(`modal.${ modalType.value }.title`));
    setModalBody(t(`modal.${ modalType.value }.body_1`));
    setModalBackText(t(`modal.${ modalType.value }.back`));
    setModalNegativeText(t(`modal.${ modalType.value }.negative`));
    setModalPositiveText(t(`modal.${ modalType.value }.positive`));
    setModalVisibility(true);
  };

  const resetModal = () => {
    setModalVisibility(false);
    setPositiveHandleTrigger(false);
    setModalErrors([]);
    setModalTitle("");
    setModalBody("");
    setModalBackText("");
    setModalBackLink();
    setModalNegativeText("");
    setModalPositiveText("");
    setValidationErrors({});
    
    positiveButtonDisabled.value = false;

    // Reset variables
    setNewVehicle(null);
    setNewUserAddress(null);
  };

  const negativeHandler = () => {
    if(modalType.value === modalTypeConstants.value.TERMS_AND_CONDITIONS) {
      setModal(modalTypeConstants.value.SIGN_UP)
    } else {
      resetModal();
    }
  };

  const convertErrorsToMessages = (errorResponse) => {
    modalErrors.value = [];
    if (errorResponse && Array.isArray(errorResponse)) {
      errorResponse.forEach((error) => {
        const detail = error.detail[0];
        const errorType = detail.error;
        const value = detail.value;

        let errorMessage = '';
        if (errorType === 'taken') {
          if (value) {
            const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
            errorMessage = `Email address ${formattedValue} is already taken.`;
          } else {
            errorMessage = 'Email address is already taken.';
          }
        } else {
          errorMessage = 'An error occurred.';
        }

        modalErrors.value.push(errorMessage);
      });
    } else if (errorResponse && errorResponse.message) {
      modalErrors.value.push(errorResponse.message);
    }
  };

  const setNewVehicle = (vehicle) => {
    newVehicle.value = vehicle;
  }

  const setNewUserAddress = (address) => {
    newUserAddress.value = address;
  }

  const setNewGarage = (vehicle) => {
    newGarage.value = vehicle;
  }

  const setImage = (value) => {
    image.value = value;
  };

  const disablePositiveButton = computed(() => {
    switch (modalType.value) {
      case modalTypeConstants.value.ADD_VEHICLE || modalTypeConstants.value.EDIT_VEHICLE:
      return !(newVehicle.year && newVehicle.year !== t('modal.add_vehicle.year_placeholder') &&
      newVehicle.make && newVehicle.make_id !== t('modal.add_vehicle.make_placeholder') &&
      newVehicle.vehicle_model_id && newVehicle.vehicle_model_id !== t('modal.add_vehicle.model_placeholder') &&
      newVehicle.vehicle_edition_id && newVehicle.vehicle_edition_id !== t('modal.add_vehicle.trim_placeholder'));
      default:
      return false;
    }
  });

  // VALIDATIONS
  const validateNewVehicle = () => {
    setValidationErrors({});

    if (newVehicle.value.year === t('modal.add_vehicle.year_placeholder')) {
      validationErrors.value.year = true;
    }
    if (newVehicle.value.make_id === t('modal.add_vehicle.make_placeholder')) {
      validationErrors.value.make_id = true;
    }
    if (newVehicle.value.vehicle_model_id === t('modal.add_vehicle.model_placeholder')) {
      validationErrors.value.vehicle_model_id = true;
    }
    if (newVehicle.value.vehicle_edition_id === t('modal.add_vehicle.trim_placeholder')) {
      validationErrors.value.vehicle_edition_id = true;
    }

    return Object.keys(validationErrors.value).length === 0 && validationErrors.value.constructor === Object;
  }

  watch(() => positiveHandleTrigger.value, (value) => {
    if (value) {
      switch (modalType.value) {
        case modalTypeConstants.value.ADD_VEHICLE:
        if (validateNewVehicle()) {
          garageStore.createVehicle(newVehicle.value);
          positiveButtonDisabled.value = true;
        } else {
          setPositiveHandleTrigger(false);
        }
        break;
        case modalTypeConstants.value.EDIT_VEHICLE:
        garageStore.updateVehicle(newVehicle.value);
        break;
        case modalTypeConstants.value.GARAGE_INFO:
        garageStore.updateGarage(newGarage.value);
        break;
        case modalTypeConstants.value.VEHICLE_INFO:
        garageStore.updateVehicle(newVehicle.value);
        break;
        case modalTypeConstants.value.DRIVETRAIN_INFO:
        garageStore.updateVehicle(newVehicle.value);
        break;
        case modalTypeConstants.value.DESIGN_INFO:
        garageStore.updateVehicle(newVehicle.value);
        break;
        case modalTypeConstants.value.DELETE_ACCOUNT:
        authStore.deleteUserAccount();
        break;
        case modalTypeConstants.value.SIGN_UP_VERIFICATION:
        setModal(modalTypeConstants.value.LOGIN)
        break;
        case modalTypeConstants.value.RESEND_VERIFICATION:
        setModal(modalTypeConstants.value.LOGIN)
        break;
        case modalTypeConstants.value.FORGOT_PASSWORD:
        authStore.passwordResetEmail = userEmail.value;
        authStore.sendPasswordResetEmail();
        setModal(modalTypeConstants.value.CHANGE_PASSWORD_CONFIRMATION_NOTICE);
        break;
        case modalTypeConstants.value.CHANGE_PASSWORD_CONFIRMATION_NOTICE:
        resetModal();
        break;
        case modalTypeConstants.value.CONFIRM_MOVE_TO_SERVICE_HISTORY:
        garageStore.moveServiceToServiceHistory(moveToServiceHistoryQuote);
        resetModal();
        break;
        case modalTypeConstants.value.ADD_ADDRESS:
        case modalTypeConstants.value.EDIT_ADDRESS:
        // Handled by saved_locations.vue component
        break;
      }

      setTimeout(() => {
        setPositiveHandleTrigger(false)
      }, 100);
    }
  });

  return {
    modalVisibility,
    modalType,
    modalTypeExtraParam,
    modalTitle,
    modalBody,
    negativeButtonText,
    positiveButtonText,
    backButtonText,
    backButtonLink,
    positiveHandleTrigger,
    positiveButtonDisabled,
    modalErrors,
    disablePositiveButton,
    modalTypeConstants,
    smallWidthModals,
    newVehicle,
    newGarage,
    newUserAddress,
    userEmail,
    userVerificationPassword,
    validationErrors,
    setModal,
    setModalVisibility,
    setModalTitle,
    setModalBody,
    setModalBackText,
    setModalBackLink,
    setModalNegativeText,
    setModalPositiveText,
    setPositiveHandleTrigger,
    resetModal,
    setImage,
    negativeHandler,
    convertErrorsToMessages,
    $reset,
    currentStripeProduct,
    moveToServiceHistoryQuote
  };
});
